import {
  CopyPlusIcon,
  DropdownMenu,
  IconButton,
  PencilIcon,
} from '@buffer-mono/popcorn'

import { PostCardContent } from '~publish/components/PostCardContent'
import { readFragment } from '~publish/graphql'
import { draftActionLabels } from '~publish/helpers/posts/draftActionLabels'
import { isOfType } from '~publish/helpers/typeGuards'
import { useDateTimeFormatter } from '~publish/hooks/useDateTimeFormatter'
import { usePostComposer } from '~publish/hooks/usePostComposer'

import { MoreActionsDropdown } from '../PostCard/PostCardActions/MoreActionsDropdown'
import { PostMediaAsset_Asset } from '../PostCard/PostMedia/PostMediaAsset'
import { ContentItemCardAuthor } from './ContentItemCardAuthor'
import { ContentItemDetailsCardHeader } from './ContentItemDetailsCardHeader'
import { ContentItemDetailsCardView } from './ContentItemDetailsCardView'
import { ContentItemMedia } from './ContentItemMedia'
import { CopyDraftLinkMenuItem } from './CopyDraftLinkMenuItem'
import { DeleteContentItemMenuItem } from './DeleteContentItemMenuItem'
import { useContentItemDraftActions } from './useContentItemDraftActions'
import type { ContentItemDraft } from './usePaginatedContentItems'

import type { JSX, ReactNode } from 'react'

/**
 * Details/preview card for a channel-less draft — the ContentItem counterpart
 * to the post details card (CalendarPostCard). Shares the visual layout via
 * PostCardView, and its header via ContentItemDetailsCardHeader (the channel
 * info CalendarPostCardHeader shows doesn't apply, but the date/status/Expand
 * pattern does). Omits the post-only metrics/tags rows. Edit, Duplicate and
 * Delete live in the footer.
 */
export function ContentItemDetailsCard({
  contentItem,
  hideExpand = false,
  notesAction,
}: {
  contentItem: ContentItemDraft
  /**
   * Set when this card is already the destination the Expand action would
   * navigate to (inside ContentItemDetailsDialog itself), so the header omits
   * a redundant self-link.
   */
  hideExpand?: boolean
  /** The draft details dialog's Notes toggle, first among the actions. */
  notesAction?: ReactNode
}): JSX.Element | null {
  const { triggerAttributes } = usePostComposer()
  const { edit, duplicate } = useContentItemDraftActions(contentItem)
  const dateTimeFormatter = useDateTimeFormatter()

  const body = isOfType(contentItem.body, 'DraftContent')
    ? contentItem.body
    : null
  if (!body) return null

  const { editLabel, moreActionsLabel } = draftActionLabels({
    text: body.text,
    title: contentItem.title,
    createdAtLabel: dateTimeFormatter(contentItem.createdAt, {
      includeTimeZone: false,
    }),
    assets: readFragment(PostMediaAsset_Asset, body.assets),
  })

  return (
    <ContentItemDetailsCardView
      header={
        hideExpand ? undefined : (
          <ContentItemDetailsCardHeader
            contentItemId={contentItem.id}
            targetDate={contentItem.targetDate}
          />
        )
      }
      content={<PostCardContent text={body.text} annotations={[]} />}
      media={
        body.assets.length > 0 ? (
          <ContentItemMedia assets={body.assets} />
        ) : undefined
      }
      author={
        <ContentItemCardAuthor
          createdAt={contentItem.createdAt}
          author={contentItem.author}
        />
      }
      actions={
        <>
          {notesAction}
          {contentItem.allowedActions.includes('updateContentItem') && (
            <IconButton
              {...triggerAttributes}
              variant="secondary"
              label={editLabel}
              tooltip="Edit"
              onClick={edit}
            >
              <PencilIcon />
            </IconButton>
          )}
          <MoreActionsDropdown label={moreActionsLabel}>
            <MoreActionsDropdown.Group>
              <CopyDraftLinkMenuItem contentItemId={contentItem.id} />
            </MoreActionsDropdown.Group>
            <MoreActionsDropdown.Group>
              {/* Duplicate writes nothing: it seeds a fresh composer, and
                  Save creates a new draft under its own authorization. */}
              <DropdownMenu.Item onSelect={duplicate}>
                <CopyPlusIcon />
                Duplicate
              </DropdownMenu.Item>
            </MoreActionsDropdown.Group>
            <MoreActionsDropdown.Group>
              <DeleteContentItemMenuItem
                contentItemId={contentItem.id}
                allowedActions={contentItem.allowedActions}
              />
            </MoreActionsDropdown.Group>
          </MoreActionsDropdown>
        </>
      }
    />
  )
}
